/**
 * AI Skills Service
 * Provides CRUD access to altzero.base_ai_skills via /api/revisr/ai-skills.
 *
 * DB schema (migration 014): tenant_id-based.
 *   tenant_id IS NULL  → global skill (read-only for tenants)
 *   tenant_id SET      → org-scoped skill
 */

import { mainClient } from '@/utils/api/mainClient';

export type AiSkillCategory = 'system' | 'policy_generation';

export type AiSkill = {
  id: string;
  tenant_id: string | null;
  skill_name: string;
  system_prompt_md: string;
  default_flag: boolean;
  category: AiSkillCategory;
  created_at?: string;
  updated_at?: string;
  // Derived for UI compatibility
  created_by: string | null;   // null when tenant_id is null (global)
  is_personal: boolean;        // always false in this schema
  organisation_ids: string[];  // always [] in this schema
};

export type CreateAiSkillData = {
  skill_name: string;
  system_prompt_md: string;
  default_flag?: boolean;
  category?: AiSkillCategory;
  // kept for UI form compat — ignored by server (server derives from org context)
  is_personal?: boolean;
  organisation_ids?: string[];
};

export type UpdateAiSkillData = Partial<CreateAiSkillData>;

const getActiveOrgId = (): string | undefined => {
  try { return localStorage.getItem('revisr_active_workspace') ?? undefined; }
  catch { return undefined; }
};

/** Normalise a raw DB row to the AiSkill shape the UI expects. */
const normalise = (raw: Record<string, unknown>): AiSkill => ({
  id: raw.id as string,
  tenant_id: (raw.tenant_id as string | null) ?? null,
  skill_name: raw.skill_name as string,
  system_prompt_md: raw.system_prompt_md as string,
  default_flag: Boolean(raw.default_flag),
  category: (raw.category as AiSkillCategory | undefined) ?? 'policy_generation',
  created_at: raw.created_at as string | undefined,
  updated_at: raw.updated_at as string | undefined,
  created_by: raw.tenant_id ? 'org' : null,  // null = global (UI checks created_by === null)
  is_personal: false,
  organisation_ids: [],
});

/** List skills visible to the current org (global + this org's skills). */
export const listAiSkills = async (): Promise<AiSkill[]> => {
  const organisationId = getActiveOrgId();
  const { data } = await mainClient.get<{ skills: Record<string, unknown>[] }>(
    '/api/revisr/ai-skills',
    { organisationId }
  );
  return (data.skills ?? []).map(normalise);
};

/** Create a new skill scoped to the current org. */
export const createAiSkill = async (
  input: CreateAiSkillData
): Promise<AiSkill> => {
  const organisationId = getActiveOrgId();
  const { data } = await mainClient.post<{ skill: Record<string, unknown> }>(
    '/api/revisr/ai-skills',
    {
      skill_name: input.skill_name,
      system_prompt_md: input.system_prompt_md,
      default_flag: input.default_flag ?? false,
      category: input.category ?? 'policy_generation',
    },
    { organisationId }
  );
  return normalise(data.skill);
};

/** Update an org-scoped skill. */
export const updateAiSkill = async (
  id: string,
  input: UpdateAiSkillData
): Promise<AiSkill> => {
  const organisationId = getActiveOrgId();
  const { data } = await mainClient.patch<{ skill: Record<string, unknown> }>(
    `/api/revisr/ai-skills/${id}`,
    {
      skill_name: input.skill_name,
      system_prompt_md: input.system_prompt_md,
      default_flag: input.default_flag,
      category: input.category,
    },
    { organisationId }
  );
  return normalise(data.skill);
};

/** Delete an org-scoped skill. */
export const deleteAiSkill = async (id: string): Promise<void> => {
  const organisationId = getActiveOrgId();
  await mainClient.delete(`/api/revisr/ai-skills/${id}`, { organisationId });
};

export const aiSkillsService = {
  listAiSkills,
  createAiSkill,
  updateAiSkill,
  deleteAiSkill,
};
